import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
  Button,
  cn,
} from '@scio3/ui';
import { DOWNGRADE_TRIAL_TO_FREE, DOWNGRADE_PAID_TO_FREE } from '@scio3/graphql-client';
import { useBillingDialogStore } from '../stores/billing-dialog-store';
import { useBillingData } from '../hooks/useBillingData';
import { extractMutationResponse } from '../utils/plan-helpers';

/**
 * Downgrade confirmation dialog — matches scio1's ConfirmDowngradeSubscriptionModal.
 * Shows device limit warning, Cancel + Downgrade buttons.
 */
export function DowngradeDialog() {
  const { t } = useTranslation();
  const activeDialog = useBillingDialogStore((s) => s.activeDialog);
  const dialogContext = useBillingDialogStore((s) => s.dialogContext);
  const closeDialog = useBillingDialogStore((s) => s.closeDialog);

  const { isTrial, currency } = useBillingData();

  // Additional backend warnings (passed via dialog context from handleSelectFree)
  const backendWarnings = (dialogContext.warnings as string[] | undefined) ?? [];
  const subAccountId = dialogContext.subAccountId as string | undefined;

  const [downgradeTrialToFree, { loading: trialLoading }] = useMutation(DOWNGRADE_TRIAL_TO_FREE, {
    refetchQueries: [
      'Me',
      'GetCompanyInfo',
      'GetUpcomingSubscription',
      'GetSubscriptionInfoStripe',
    ],
  });
  const [downgradePaidToFree, { loading: paidLoading }] = useMutation(DOWNGRADE_PAID_TO_FREE, {
    refetchQueries: [
      'Me',
      'GetCompanyInfo',
      'GetUpcomingSubscription',
      'GetSubscriptionInfoStripe',
    ],
  });

  const isSubmitting = trialLoading || paidLoading;

  const handleDowngrade = useCallback(async () => {
    try {
      if (isTrial) {
        const result = await downgradeTrialToFree({
          variables: { subAccountId, currency },
        });
        const response = extractMutationResponse<{
          status?: boolean;
          message?: string;
          code?: number;
        }>(result.data, 'downgradeTrialToFreePlan');
        if (response?.status) {
          toast(t('Your plan has been downgraded to Free'));
          closeDialog();
        } else {
          toast.error(response?.message ?? t('Failed to downgrade'));
        }
      } else {
        const result = await downgradePaidToFree({
          variables: { subAccountId },
        });
        const response = extractMutationResponse<{
          status?: boolean;
          message?: string;
          code?: number;
        }>(result.data, 'downgradePaidToFreePlan');
        if (response?.status) {
          toast.success(
            t('Your subscription will be canceled at the end of the current billing period'),
          );
          closeDialog();
        } else {
          toast.error(response?.message ?? t('Failed to downgrade'));
        }
      }
    } catch (err) {
      console.error('Downgrade failed:', err);
      toast.error(t('Failed to downgrade'));
    }
  }, [isTrial, downgradeTrialToFree, downgradePaidToFree, closeDialog, t, subAccountId, currency]);

  const isOpen = activeDialog === 'downgrade';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent
        className="max-h-[85vh] flex flex-col overflow-hidden sm:max-w-md"
        data-testid="downgrade-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            <AlertTriangle className="h-5 w-5 text-destructive" />
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          {/* Static device limit warning — matching scio1 freePlanDowngradeMoveToAndCancel */}
          <p className="text-sm">{t('freePlanDowngradeMoveToAndCancel')}</p>

          {/* Additional backend warnings (if any) */}
          {backendWarnings.map((msg, i) => (
            <p key={i} className="text-sm">
              {t(msg)}
            </p>
          ))}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" className="cursor-pointer">
              {t('Cancel')}
            </Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={handleDowngrade}
            disabled={isSubmitting}
            className={cn('cursor-pointer', isSubmitting && 'opacity-50 cursor-not-allowed')}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('Cancel Subscription & Downgrade')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
